import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { refreshCartPageWithSaveProductCart } from "../redux/actions/action-creator";
import { isGuestUser } from "../utils/guestLogin";
import { getMarketMetadata } from "../utils/locationDefaults";
import { getSegmentItems } from "../utils/segmentFlow";
import { getCartCurrency } from "../utils/cartCurrency";
import "./cart-preview-bar.css";

const parse = (value) => {
  if (typeof value !== "string") return value;
  try { return JSON.parse(value); } catch { return null; }
};
const number = (value) => {
  const parsed = Number(String(value ?? "").replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};
const firstValue = (...values) => values.find((value) => value !== null && value !== undefined && value !== "");
const itemKey = (item) => `${item.product_variant_id || item.variant_id || item.id || ""}:${item.portion || ""}`;
const cartSignature = (items) => items.map((item) => `${itemKey(item)}:${number(item.quantity ?? 1)}`).sort().join("|");
const unitPrice = (item) => firstValue(item.price, item.selling_price, item.product_price);
const storeId = (store) => firstValue(store?.business_segment_id, store?.businessSegmentId, store?.store_id, store?.storeId, store?.id);
const explicitStoreName = (source) => firstValue(source?.store_name, source?.business_segment_name, source?.business_name, source?.store_title);

export default function CartPreviewBar() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname, state: routeState } = useLocation();
  const cart = useSelector((state) => state.cart || {});
  const storesData = useSelector((state) => state.common?.storesData);
  const { userData, user_location } = useSelector((state) => state.auth || {});
  const [opening, setOpening] = useState(false);
  const [footerOffset, setFooterOffset] = useState(0);
  const openingRef = useRef(false);
  const summary = cart.serverCartSummary;
  const items = summary ? summary.product_details : cart.cartItems;
  const snapshotProducts = Array.isArray(items) ? items.filter((item) => number(item.quantity ?? 1) > 0) : [];
  const confirmedProducts = Array.isArray(cart.cartItems) ? cart.cartItems : [];
  const matchesConfirmed = summary && cart.cartId && String(summary.cart_id) === String(cart.cartId) &&
    cartSignature(snapshotProducts) === cartSignature(confirmedProducts);
  const products = matchesConfirmed ? snapshotProducts.map((item) => ({
    ...confirmedProducts.find((confirmed) => itemKey(confirmed) === itemKey(item)), ...item,
  })) : snapshotProducts;
  const confirmedReceipt = parse(cart.data?.receipt) || {};
  const receipt = summary
    ? { ...(matchesConfirmed ? confirmedReceipt : {}), ...(parse(summary.receipt) || {}) }
    : confirmedReceipt;
  const quantity = products.reduce((total, item) => total + number(item.quantity ?? 1), 0);
  const itemTotal = products.every((item) => unitPrice(item) !== undefined)
    ? products.reduce((total, item) => total + number(unitPrice(item)) * number(item.quantity ?? 1), 0)
    : undefined;
  const amount = firstValue(receipt.total_amount, receipt.item_total, receipt.subtotal,
    !summary || matchesConfirmed ? cart.data?.cart_amount : undefined, itemTotal);
  const currency = getCartCurrency(summary) ||
    (!summary || matchesConfirmed ? getCartCurrency(cart.data) : "") ||
    getCartCurrency(products[0]) || getMarketMetadata(user_location || {}).currency;
  const sameCart = !summary || (cart.cartId && String(summary.cart_id) === String(cart.cartId));
  const cartData = sameCart ? cart.data || {} : {};
  const businessId = firstValue(summary?.business_segment_id, cartData.business_segment_id,
    cartData.businessSegmentId, cartData.store_id, receipt.business_segment_id,
    products.find((item) => item.business_segment_id)?.business_segment_id);
  const matchingStore = businessId !== undefined ? [
    ...getSegmentItems(storesData), routeState?.store,
  ].find((store) => store && String(storeId(store)) === String(businessId)) : null;
  const nestedStore = parse(cartData.store || cartData.store_details || cartData.business_segment_details);
  const storeName = firstValue(explicitStoreName(summary), explicitStoreName(cartData), explicitStoreName(receipt),
    explicitStoreName(nestedStore), nestedStore?.title, nestedStore?.name,
    explicitStoreName(matchingStore), matchingStore?.title, matchingStore?.name);
  const home = pathname === "/home" || pathname === "/";
  const visibleRoute = home || /^\/(stores|shop|category|store-category)(\/|$)/.test(pathname);
  const visible = visibleRoute && !isGuestUser(userData) && products.length > 0;

  useEffect(() => {
    setFooterOffset(0);
    if (!visible) return undefined;
    const footer = document.querySelector(".bottom-footer");
    if (!footer) return undefined;

    document.body.classList.add("has-cart-preview");
    let frame = null;
    const measure = () => {
      frame = null;
      const rect = footer.getBoundingClientRect();
      const mobileHome = home && window.innerWidth < 992;
      setFooterOffset(mobileHome ? 0 : Math.ceil(Math.max(0, Math.min(rect.height, window.innerHeight - rect.top))));
    };
    const scheduleMeasure = () => {
      if (frame === null) frame = window.requestAnimationFrame(measure);
    };
    const observer = typeof IntersectionObserver !== "undefined"
      ? new IntersectionObserver(scheduleMeasure, { threshold: [0, 0.25, 0.5, 0.75, 1] })
      : null;

    measure();
    observer?.observe(footer);
    window.addEventListener("scroll", scheduleMeasure, { passive: true });
    window.addEventListener("resize", scheduleMeasure);
    return () => {
      document.body.classList.remove("has-cart-preview");
      observer?.disconnect();
      window.removeEventListener("scroll", scheduleMeasure);
      window.removeEventListener("resize", scheduleMeasure);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [home, pathname, visible]);

  const openCart = async () => {
    if (openingRef.current || cart.saveCartLoading || cart.getCartLoading) return;
    openingRef.current = true;
    setOpening(true);
    try {
      if (await dispatch(refreshCartPageWithSaveProductCart())) navigate("/cart");
    } finally {
      openingRef.current = false;
      setOpening(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="cart-preview-root" style={{ "--cart-preview-footer-offset": `${footerOffset}px` }}>
      <aside className={`cart-preview-bar${home ? " cart-preview-bar--home" : ""}`} aria-label="Your basket">
        <div className="cart-preview-bar__summary" aria-live="polite" aria-atomic="true">
          <span className="cart-preview-bar__icon" aria-hidden="true"><i className="ph ph-shopping-bag" /></span>
          <span className="cart-preview-bar__details">
            <strong className="cart-preview-bar__store" title={storeName}>{storeName || "Your basket"}</strong>
            <span className="cart-preview-bar__totals">
              <span>{quantity} {quantity === 1 ? "item" : "items"}</span>
              {amount !== undefined && <>
                <span aria-hidden="true">·</span>
                <span className="cart-preview-bar__amount">{currency} {number(amount).toFixed(2)}</span>
              </>}
            </span>
          </span>
        </div>
        <button type="button" onClick={openCart} disabled={opening || cart.saveCartLoading || cart.getCartLoading}>
          {opening ? "Opening cart..." : "View cart"}
          <i className="ph ph-arrow-right" aria-hidden="true" />
        </button>
      </aside>
    </div>
  );
}
